import type { CSSProperties, HTMLAttributes, ReactNode } from "react";
import { Trash2 } from "lucide-react";
import { AddRow } from "../AddRow/AddRow.js";
import { Icon } from "../Icon/Icon.js";
import { DragHandle } from "../Reorder/DragHandle.js";
import { useDropTarget } from "../Reorder/reorder.js";
import styles from "./RecordList.module.scss";

/**
 * A reorderable list of small records — the EPK's press quotes, points of contact and notable moments, which
 * are the same thing (add, edit in place, reorder, remove) and used to be three different controls: a boxed stack
 * with a top-right trash, an unreorderable inline row, and a headed table with a ✕. One shape now, the Link in
 * Bio row's (the card directly above them): each record its own bordered row — grip · fields · trash — with
 * "Add …" beneath and an optional "n / max" count.
 *
 * Order is the array order (the server derives `position` from it), so a move is a splice the caller owns.
 * `layout` is the per-list choice: `inline` puts short fields side by side (wrapping to a stack on a phone),
 * `stack` stacks them at every width — a quote is a paragraph and never belongs on one line with its source.
 *
 * `columns` is the one exception, for a list whose rows are a TABLE (a release's writers: name · affiliation · IPI ·
 * split): fixed columns at every width with a header row above them, laid out with the row's own grip and trash
 * spacing so each label sits over its field. The header is visual only — every field still carries its own label.
 *
 * `draggable={false}` drops the grip and the drop target, for a list whose order is not the user's to change.
 * `borderStyle="lite"` drops the row's border, and its fields lose their left padding and box — a bottom rule
 * on hover/focus only (Field's `data-field-style="underline"`) — for a
 * list that sits inside a section rather than standing as its own card (a release's credits).
 */
export function RecordList<T>({
  dragKind,
  items,
  layout = "inline",
  columns,
  draggable = true,
  borderStyle = "full",
  max,
  showCount = false,
  addLabel,
  empty,
  nameOf,
  renderFields,
  onAdd,
  onMove,
  onRemove,
}: {
  /** The drag-and-drop kind — distinct per list, so a quote cannot be dropped into the contacts. */
  dragKind: string;
  items: readonly T[];
  layout?: "inline" | "stack";
  /** Table columns: a header label and a CSS track size each (default `minmax(0, 1fr)`). Overrides `layout`. */
  columns?: readonly { label: string; width?: string; align?: "start" | "end" }[];
  /** Rows can be reordered (grip + drop target). Default true. */
  draggable?: boolean;
  /** `full`: each row boxed. `lite`: no row border; fields unpadded on the left, bottom rule on hover/focus. Default `full`. */
  borderStyle?: "full" | "lite";
  /** The cap: "Add" disappears once it is reached. */
  max?: number;
  /** Show "n / max" beside the add button (worth it for a short cap; noise for a long one). */
  showCount?: boolean;
  addLabel: string;
  /** A line shown when the list is empty, above the add button. */
  empty?: ReactNode;
  /** The record's name for screen readers — the grip and the remove button say WHICH record they act on. */
  nameOf: (item: T, index: number) => string;
  renderFields: (item: T, index: number) => ReactNode;
  onAdd: () => void;
  onMove: (from: number, to: number) => void;
  onRemove: (index: number) => void;
}) {
  const full = max !== undefined && items.length >= max;
  const fieldLayout = columns ? "columns" : layout;
  const columnStyle = columns
    ? ({ "--record-cols": columns.map((c) => c.width ?? "minmax(0, 1fr)").join(" ") } as CSSProperties)
    : undefined;
  return (
    <div className={styles.list} style={columnStyle} data-border={borderStyle}
      data-field-style={borderStyle === "lite" ? "underline" : undefined}>
      {items.length === 0 && empty && <p className={styles.empty}>{empty}</p>}
      {items.length > 0 && columns && (
        <div className={styles.head} aria-hidden="true">
          {draggable && <span className={styles.gripSpace} />}
          <div className={styles.fields} data-layout="columns">
            {columns.map((c) => (
              <span key={c.label} className={styles.headCell} data-align={c.align === "end" ? "end" : undefined}>
                {c.label}
              </span>
            ))}
          </div>
          <span className={styles.removeSpace} />
        </div>
      )}
      {items.length > 0 && (
        <ol className={styles.rows}>
          {items.map((item, i) => (
            <RecordRow key={i} index={i} count={items.length} dragKind={dragKind} draggable={draggable} layout={fieldLayout}
              name={nameOf(item, i)} onMove={onMove} onRemove={() => onRemove(i)}>
              {renderFields(item, i)}
            </RecordRow>
          ))}
        </ol>
      )}
      {(!full || showCount) && (
        <div className={styles.foot}>
          {!full && <AddRow onClick={onAdd}>{addLabel}</AddRow>}
          {showCount && max !== undefined && (
            <span className={styles.count} data-full={full || undefined}>
              {items.length} / {max}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

/** One record. The grip is the drag source AND the keyboard control; the ROW is the drop target — `draggable`
 *  never sits on the row itself, because it holds text inputs and would fight text selection. */
function RecordRow({ draggable, ...props }: RecordRowProps & { draggable: boolean }) {
  return draggable ? <DraggableRecordRow {...props} /> : <RowBody {...props} />;
}

type RecordRowProps = {
  index: number;
  count: number;
  dragKind: string;
  layout: "inline" | "stack" | "columns";
  name: string;
  onMove: (from: number, to: number) => void;
  onRemove: () => void;
  children: ReactNode;
};

// The drop-target hook lives here, so a non-draggable row never registers one.
function DraggableRecordRow(props: RecordRowProps) {
  const { index, count, dragKind, name, onMove } = props;
  const drop = useDropTarget({ index, kind: dragKind, onMove });
  return (
    <RowBody {...props} rowProps={drop}
      grip={<DragHandle label={name} index={index} count={count} kind={dragKind} onMove={onMove} />} />
  );
}

function RowBody({ layout, name, onRemove, children, grip, rowProps }: RecordRowProps & {
  grip?: ReactNode;
  rowProps?: HTMLAttributes<HTMLLIElement>;
}) {
  return (
    <li className={styles.row} {...rowProps}>
      {grip}
      <div className={styles.fields} data-layout={layout}>{children}</div>
      <button type="button" className={styles.remove} aria-label={`Remove ${name}`} onClick={onRemove}>
        <Icon icon={Trash2} size={16} />
      </button>
    </li>
  );
}
